import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API, UTIL*/
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'

/** STYLED */
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import OperativeApi from 'api/setting/treatments/operative.api'
import { selectMe } from 'app/slice/user.slice'
import { notiError } from 'component/notifications/notifications'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import { useSelector } from 'react-redux'
import { getBranch, toBuddhistYear } from 'utils/app.utils'
import ExportToExcel from './ExportToExcel'
import PatientsTreatmentList from './PatientsTreatmentList'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function PatientsTreatment() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const profile = useSelector(selectMe)
  const [branchId, setBranchId] = useState<number>(getBranch())
  const [branches, setBranches] = useState<any[]>([])

  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')

  const [patientsTreatmentData, setPatientsTreatmentData] = useState<any>([])
  const [patientsTreatmentDataExport, setPatientsTreatmentDataExport] = useState<any>([])
  const [patientsTreatmentPage, setPatientsTreatmentPage] = useState<number>(1)
  const [patientsTreatmentTypePage, setPatientsTreatmentTypePage] = useState<number>(1)
  const [patientsTreatmentPageLimit, setPatientsTreatmentPageLimit] = useState<number>(10)
  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])

  const [fetchTrigger, setFetchTrigger] = useState<number>(0)
  //master data
  const [operatives, setOperative] = useState<any[]>([])
  const [operativeType, setOperativeType] = useState<any[]>([])

  //filter state
  const [operativeId, setOperativeId] = useState<any>(0)
  const [operativeTypeId, setOperativeTypeId] = useState<any>(null)

  const [rangeDate, setRangeDate] = useState<string[]>([moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])
  const [duplicatePatient, setDuplicatePatient] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)
  const filter = [branchId === 0 ? t(`REPORT.FILTER.ALL`) : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`]

  const exportExcel = async (data?: any) => {
    setLoading(true);
    try {

      const doctorNames = selectDoctors[0] !== 'ALL'
        ? doctors
          .filter((d: any) => selectDoctors.includes(d.userId))
          ?.map((d: any) => d.fullname)
          .slice(0, 3)
          .join(', ')
        : t(`REPORT.FILTER.ALL`)

      const operativeTypeName = operativeTypeId?.operativeTypeName || t(`REPORT.FILTER.ALL`)
      const operativeName = operativeId?.operativeName || t(`REPORT.FILTER.ALL`)
      await ExportToExcel({
        data: data || patientsTreatmentDataExport,
        branches,
        branchId,
        filter: {
          doctorNames,
          operativeTypeName,
          operativeName,
          duplicatePatient,
        },
        filterDateStart: rangeDate[0],
        filterDateEnd: rangeDate[1],
        t,
        profile
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setLoading(false);
    }
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'PATIENT_TREATMENT') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })



    OperativeApi.findAll({ export: 1 })
      .then(({ data }) => {
        setOperative([{ operativeName: t('REPORT.FILTER.OPERATION_ALL'), operativeId: 0 }, ...data])
      })
      .catch((e) => {
        return
      })

    OperativeTypeApi.findAll({ export: 1 })
      .then(({ data }) => {
        setOperativeType([{ operativeTypeName: t('REPORT.FILTER.TYPE_OPERATION_ALL'), operativeTypeId: 0 }, ...data])
      })
      .catch((e) => {
        return
      })
    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [])

  useEffect(() => {
    const bId = branchId === 0 ? undefined : branchId
    ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1], bId)
      .then((resp) => {
        setDoctors([...resp.data])
        setSelectDoctors(['ALL', ...resp.data.map((d: any) => d.userId)])
      })
      .catch((e) => {
        return
      })
  }, [branchId])

  const fetchPatientTreatmentExport = async () => {
    setLoading(true)

    let condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 10000,
      branchId: branchId,
      export: 1,
      duplicatePatient: duplicatePatient,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1]
    }
    const opTypeId = operativeTypeId?.operativeTypeId ?? operativeTypeId;
    if (opTypeId) condition = { ...condition, operativeTypeId: opTypeId }

    const opId = operativeId?.operativeId ?? operativeId;
    if (opId) condition = { ...condition, operativeId: opId }

    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    return await ReportApi.reportPatientTreatment(condition)
      .then(({ headers, data }) => {
        setPatientsTreatmentDataExport(data)
        return data
      })
      .catch((e) => {
        setLoading(false)
      })
      .finally(() => setLoading(false))
  }

  const fetchPatientTreatment = useCallback(() => {
    setLoading(true)
    let condition: FindAllReportInterface = {
      page: patientsTreatmentPage,
      pageLimit: patientsTreatmentPageLimit,
      branchId: branchId,
      duplicatePatient: duplicatePatient,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1]
    }

    const opTypeId = operativeTypeId?.operativeTypeId ?? operativeTypeId;
    if (opTypeId) condition = { ...condition, operativeTypeId: opTypeId }

    const opId = operativeId?.operativeId ?? operativeId;
    if (opId) condition = { ...condition, operativeId: opId }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }

    ReportApi.reportPatientTreatment(condition)
      .then(({ headers, data }) => {
        setPatientsTreatmentData(data)
        setRowCount(headers['x-total'])
      })
      .catch((e) => {
        setLoading(false)
      })
      .finally(() => setLoading(false))
  }, [fetchTrigger, patientsTreatmentPage, patientsTreatmentPageLimit])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }
  const handleChangeRowsPerPage = (limit: number) => setPatientsTreatmentPageLimit(limit)
  const handleChangePage = (val: number) => setPatientsTreatmentPage(val)
  useEffect(() => {
    fetchPatientTreatment()
  }, [fetchPatientTreatment])

  const onSubmit = () => {
    if (rangeDate[0] === '' || rangeDate[1] === '') {
      notiError(t(`REPORT.MESSAGE.INVALID_DATE`))
    } else {
      setFetchTrigger(prev => prev + 1)
      setLoading(true)
      setPatientsTreatmentPage(1)
    }
  }

  const getFilterUser = () => {
    if (_.includes(selectDoctors, 'ALL')) return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    else {
      return ` ${doctorsMultiple
        .filter((d: DentistInterface) => selectDoctors.includes(d.dentistId))
        ?.map((d: DentistInterface) => d.dentistFullname)
        .join(', ')}`
    }
  }

  const handleExport = async (type: string) => {
    setLoading(true)
    try {
      const data = await fetchPatientTreatmentExport()
      if (data) {
        if (type === 'Excel') {
          await exportExcel(data)
        } else if (type === 'PDF') {
          await new Promise(resolve => setTimeout(resolve, 500))
          const style = document.createElement("style")
          style.innerHTML = `
            @media print {
              @page { size: landscape }
            }
          `
          document.head.appendChild(style)
          window.print()
          document.head.removeChild(style)
        }
      }
    } catch (error) {
      console.error("Export Error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Loading show={loading} type="fullLoading" />
      <HeaderCard text={t(`REPORT.REPORT_NAME.PATIENT_TREATMENT`)} />
      <Box padding="16px">
        <Box padding="16px">
          <Grid container spacing="8px">
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectInput
                label={t('REPORT.FILTER.BRANCH')}
                value={branchId}
                onChange={e => {
                  setBranchId(e.target.value)
                  setPatientsTreatmentPage(1)
                }}
                options={[
                  ...branches.map(b => ({
                    value: b.branchId,
                    name: b.branchName || b.branchNameEn || ''
                  }))]}
                width="100%"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectCheckBoxInput
                label={t('USER_DOCTOR')}
                options={doctors.map((d: any) => ({
                  id: d.userId,
                  name: d.fullname
                }))}
                selected={selectDoctors}
                onChange={(ids: any) => {
                  setSelectDoctors(ids)
                }}
                fullWidth
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputSecondNewRangePicker
                inputHeight={40}
                value={rangeDate || emptyRangePicker}
                onchange={(val: any) => {
                  if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                  else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                }}
                label={''}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} xl={6}>
              <CheckboxButton
                onChange={() => {
                  setDuplicatePatient(!duplicatePatient)
                }}
                label={t('REPORT.FILTER.DUPLICATE_PATIENT')}
                checked={duplicatePatient}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectInput
                label={t('REPORT.FILTER.OPERATIVE_TYPE')}
                value={operativeTypeId || 0}
                onChange={e => {
                  setOperativeId({ operativeName: t('REPORT.FILTER.OPERATION_ALL'), operativeId: 0 })
                  setOperativeTypeId(e.target.value)
                }}
                options={[
                  ...operativeType.map((b: any) => ({
                    value: b.operativeTypeId,
                    name: b.operativeTypeName
                  }))]}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectInput
                label={t('REPORT.TABLE.FOLLOW_UP.OPERATIVE')}
                value={operativeId || 0}
                onChange={e => {
                  setOperativeId(e.target.value)
                }}
                options={[{ name: t(`REPORT.FILTER.OPERATION_ALL`), value: 0 },
                ..._.map(
                  operatives.filter((item) => item.operativeTypeId === operativeTypeId),
                  (b: any, index) => ({
                    value: b.operativeId,
                    name: b.operativeName
                  })
                )
                ]}
                width="100%"
                disabled={!operativeTypeId}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} xl={8}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <MainButton
                  title={t('INPUT.SEARCH')}
                  onClick={() => onSubmit()}
                  type={'primary'}
                  variant={'contained'}
                  style={{ width: '120px' }}
                />
                <DropdownButton
                  title='พิมพ์เอกสาร'
                  onChange={handleExport}
                  options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]}
                />
              </Box>
            </Grid>
          </Grid>
          <Box paddingTop="16px">
            <PatientsTreatmentList
              page={patientsTreatmentPage}
              pageLimit={patientsTreatmentPageLimit}
              rowCount={rowCount}
              sortBy={sortBy}
              sortType={sortType}
              data={patientsTreatmentData}
              dataExport={patientsTreatmentDataExport}
              onRequestSort={onRequestSort}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
              componentRef={componentRef}
              headCellsSeconds={filter}
            />
          </Box>
        </Box>

        <Box width="50%" margin="16px">
          <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
            <Table aria-label="simple table" sx={{ width: '100%' }}>
              <TableHead sx={{ backgroundColor: "#2F4269", color: 'white', border: '1px solid #2F4269' }}>
                <TableRow>
                  <TableCell sx={{ fontSize: '16px !important', padding: '15px !important', color: "white !important", fontWeight: 500, width: '80%' }}>{t('ประเภทและรายการหัตถการ')}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '16px !important', paddingX: '15px !important', paddingY: '10px !important', color: "white !important", fontWeight: 500, width: '20%' }}>{t('จำนวน (ครั้ง)')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patientsTreatmentData?.summary?.map((data: any, index: number) => (
                  <>
                    <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                      <TableCell sx={{ fontSize: '16px !important', fontWeight: 'bold !important' }}>{data?.operativeTypeName}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '16px !important', fontWeight: 'bold !important' }}>{data?.countAll}</TableCell>
                    </TableRow>
                    {_.map(data?.operatives, (item, index) => (
                      <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                        <TableCell sx={{ fontSize: '16px !important', fontWeight: 400, paddingLeft: '48px !important' }}>{item?.operativeName}</TableCell>
                        <TableCell align='right' sx={{ fontSize: '16px !important', fontWeight: 400 }}>{item?.countOperative}</TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t('REPORT.REPORT_NAME.PATIENT_TREATMENT')}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.BRANCH')}: {_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn') || 'ทั้งหมด'}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.DOCTOR')}: {getFilterUser()}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.OPERATIVE_TYPE')}: {_.find(operativeType, (item) => item.operativeTypeId === operativeTypeId)?.operativeTypeName || 'ทั้งหมด'} </Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.TYPE_OPERATION')}: {_.get(_.find(operatives, { operativeId }), 'operativeName') || 'ทั้งหมด'}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('ไม่แสดงชื่อผู้ป่วยซ้ำ')}: {duplicatePatient ? t('ไม่แสดงชื่อผู้ป่วยซ้ำ') : t('แสดงชื่อผู้ป่วยซ้ำ')}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.DATE_START')}: {toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY')}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.DATE_END')}: {toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY')}</Typography>
        </Box>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.#NO')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.DATE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.CN')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.PT')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.PATIENT_FULL_NAME')}</th>
              {profile?.hasShowPhoneNumber === "PERMISS" && <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.PHONE')}</th>}
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.DOCTOR_FULL_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE_TYPE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE_CODE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PATIENT_TREATMENT.OPERATIVE')}</th>
            </tr>
          </thead>
          <tbody>
            {patientsTreatmentDataExport?.rows?.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data?.date}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '85px' }}>{data?.cn}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '100px' }}>{data?.pt}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.patientFullName}</td>
                {profile?.hasShowPhoneNumber === "PERMISS" && <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.phone}</td>}
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.doctorFullName}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeTypeName}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', width: '85px' }}>{data?.operativeCode}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeName}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <br />

        <table style={{ width: '50%', border: '1px solid black', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('ประเภทและรายการหัตถการ')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('จำนวน (ครั้ง)')}</th>
            </tr>
          </thead>
          <tbody>
            {patientsTreatmentDataExport?.summary?.map((data: any, index: number) => (
              <>
                <tr key={index}>
                  <td style={{ fontSize: '9.5px', fontWeight: 'bold', textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeTypeName}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 'bold', textAlign: 'center', verticalAlign: 'top' }}>{data?.countAll}</td>
                </tr>
                {_.map(data?.operatives, (item, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top', paddingLeft: '24px' }}>{item?.operativeName}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '100px' }}>{item?.countOperative}</td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
